import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";

import { useParams } from "react-router-dom";

import useAccount from "../../../../../hooks/useAccount";
import { useProject } from "../../../../../hooks/useProject";
import {
  type ProjectUserAssignableRole,
  ProjectUserAssignableRoles,
  ProjectUserRoleDisplayName,
  useCreateProjectInvitation,
  useProjectSharing,
} from "../../../../../hooks/useProjectSharing";
import { useTeam } from "../../../../../hooks/useTeams";
import { useTracker } from "../../../../../hooks/useTracker";
import { EmailInvitationForm, type EmailInvitationFormHandle } from "./EmailInvitationForm";

interface EmailInvitationFormProjectProps {
  isProRoleEligible?: (role: ProjectUserAssignableRole) => boolean;
  isDisabled?: boolean;
}

export const EmailInvitationFormProject = forwardRef<
  EmailInvitationFormHandle,
  EmailInvitationFormProjectProps
>(({ isProRoleEligible, isDisabled }, ref) => {
  const tracker = useTracker();
  const { projectId } = useParams();
  const [invalidEmailList, setInvalidEmailList] = useState<string[]>([]);
  const emailFormRef = useRef<EmailInvitationFormHandle>(null);
  const {
    mutateAsync: mutateCreateProjectInvitation,
    isPending: isProjectInvitationPending,
    isError: isProjectInvitationError,
  } = useCreateProjectInvitation({
    onSuccess: (data) => {
      if (data.errors.length > 0) {
        setInvalidEmailList(data.errors.map((error) => error.email));
      } else {
        setInvalidEmailList([]);
        emailFormRef.current?.clearForm();
      }
    },
    onError: () => {
      setInvalidEmailList([]);
    },
  });

  const { data: projectSharingData } = useProjectSharing();
  const { data: projectData } = useProject();
  const { teamData } = useTeam(projectData?.teamId);
  const { data: accountData } = useAccount();
  const currentEmails = useMemo(() => {
    return [
      ...(projectSharingData?.users.map((user) => user.email) ?? []),
      ...(accountData?.email ? [accountData.email] : []),
    ];
  }, [projectSharingData, accountData]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      emailFormRef.current?.focus();
    },
    blur: () => {
      emailFormRef.current?.blur();
    },
    clearForm: () => {
      emailFormRef.current?.clearForm();
      setInvalidEmailList([]);
    },
  }));

  return (
    <EmailInvitationForm
      ref={emailFormRef}
      roles={ProjectUserAssignableRoles}
      roleDisplayName={ProjectUserRoleDisplayName}
      onSubmit={(data) => {
        return mutateCreateProjectInvitation(data);
      }}
      isDisabled={isDisabled}
      isPending={isProjectInvitationPending}
      defaultRole={"editor"}
      invalidEmailList={[...currentEmails, ...invalidEmailList]}
      isError={isProjectInvitationError || invalidEmailList.length > 0}
      proRoles={new Set<ProjectUserAssignableRole>(["editor"])}
      isProRoleEligible={isProRoleEligible}
      onEmailAdded={() => {
        tracker.trackShareModalEmailAdded({
          remoteProjectId: projectId,
          sharedResource: "project",
          spaceId: projectData?.space?.spaceId,
          projectUserRole: projectData?.userRole,
          teamUserRole: teamData?.role,
        });
      }}
      onInvalidEmailAdded={() => {
        tracker.trackShareModalInvalidEmailAdded({
          remoteProjectId: projectId,
          sharedResource: "project",
          spaceId: projectData?.space?.spaceId,
          projectUserRole: projectData?.userRole,
          teamUserRole: teamData?.role,
        });
      }}
      onRoleChanged={(newValue: string, oldValue: string) => {
        tracker.trackShareModalInviteRoleChanged({
          newValue: newValue,
          oldValue: oldValue,
          remoteProjectId: projectId,
          sharedResource: "project",
          spaceId: projectData?.space?.spaceId,
          projectUserRole: projectData?.userRole,
          teamUserRole: teamData?.role,
        });
      }}
      onEmailRemoved={() => {
        tracker.trackShareModalEmailRemoved({
          remoteProjectId: projectId,
          sharedResource: "project",
          spaceId: projectData?.space?.spaceId,
          projectUserRole: projectData?.userRole,
          teamUserRole: teamData?.role,
        });
      }}
      onInvitedPressed={(role) => {
        tracker.trackShareModalInvitePressed({
          remoteProjectId: projectId,
          role: role,
          sharedResource: "project",
          spaceId: projectData?.space?.spaceId,
          projectUserRole: projectData?.userRole,
          teamUserRole: teamData?.role,
        });
      }}
    />
  );
});
